import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Clock, History, Info, Play, Settings2, SkipForward, Undo2 } from 'lucide-react';
import clsx from 'clsx';
import type {
  JobFrequency,
  JobSchedule,
  JobState,
  ScheduledJobDto,
  ScheduledJobRunDto,
  ScheduledJobsResponse,
} from '@bbl/validation';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useToast } from '../lib/toast';
import { useConfirm } from '../components/ui/Confirm';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { Drawer } from '../components/ui/Drawer';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { SkeletonCard } from '../components/ui/Skeleton';
import { formatWhen } from '../components/teacher-reports/format';
import { EmailBanner } from '../components/system/EmailStatus';
import { runNowQuestion, runWhen } from '../components/system/run-now';

export const Route = createFileRoute('/_authed/system/scheduled-jobs')({
  component: ScheduledJobsPage,
});

const card = 'rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)]';
const pill = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap';
const selectClass =
  'w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 py-2 text-sm focus:border-gold-500 focus:outline-none focus:ring-1 focus:ring-gold-500 disabled:opacity-60';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const FREQUENCY_LABEL: Record<JobFrequency, string> = { hourly: 'Every hour', daily: 'Every day', weekly: 'Every week', monthly: 'Every month' };

/** 21 → "9:00 PM" */
const hourWords = (h: number) => `${h % 12 === 0 ? 12 : h % 12}:00 ${h < 12 ? 'AM' : 'PM'}`;
const ordinal = (n: number) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th'}`;

function scheduleWords(s: JobSchedule): string {
  const at = s.hour === null ? '' : ` at ${hourWords(s.hour)}`;
  switch (s.frequency) {
    case 'hourly':
      return 'Every hour';
    case 'daily':
      return `Every day${at}`;
    case 'weekly':
      return `Every ${WEEKDAYS[s.weekday ?? 0]}${at}`;
    case 'monthly':
      return `Every month on the ${ordinal(s.dayOfMonth ?? 1)}${at}`;
  }
}

const STATE: Record<JobState, { label: string; tone: string }> = {
  ok: { label: 'Done', tone: 'bg-[color:var(--color-success)]/15 text-[color:var(--color-success)]' },
  skipped: { label: 'Skipped', tone: 'bg-[color:var(--color-surface-strong)] text-[color:var(--color-text-muted)]' },
  running: { label: 'Running now', tone: 'bg-gold-500/15 text-gold-700 dark:text-gold-300' },
  partial: { label: 'Partly done — continuing', tone: 'bg-gold-500/15 text-gold-700 dark:text-gold-300' },
  retrying: { label: 'Failed — trying again', tone: 'bg-[color:var(--color-warning)]/15 text-[color:var(--color-warning)]' },
  failed: { label: 'Failed', tone: 'bg-[color:var(--color-danger)]/12 text-[color:var(--color-danger)]' },
  waiting: { label: 'Waiting', tone: 'bg-[color:var(--color-surface-strong)] text-[color:var(--color-text-muted)]' },
  off: { label: 'Off', tone: 'bg-[color:var(--color-surface-strong)] text-[color:var(--color-text-muted)]' },
  never: { label: 'Not run yet', tone: 'bg-[color:var(--color-surface-strong)] text-[color:var(--color-text-muted)]' },
};

const RUN_LABEL: Record<ScheduledJobRunDto['status'], { label: string; tone: string }> = {
  ok: STATE.ok,
  skipped: { label: 'Skipped', tone: STATE.off.tone },
  partial: { label: 'Partly done', tone: STATE.partial.tone },
  running: STATE.running,
  failed: { label: 'Failed', tone: STATE.retrying.tone },
  gave_up: { label: 'Stopped trying', tone: STATE.failed.tone },
};

function ScheduledJobsPage() {
  const canView = useAuthStore((s) => s.hasRole('super_admin', 'academic_admin', 'finance_admin', 'support_viewer'));
  if (!canView) {
    return (
      <ListShell title="Scheduled jobs">
        <EmptyState title="Admins only" description="Super admins manage the portal’s scheduled jobs." icon={<Clock size={32} aria-hidden="true" />} />
      </ListShell>
    );
  }
  return <ScheduledJobs />;
}

function ScheduledJobs() {
  const canEdit = useAuthStore((s) => s.hasRole('super_admin'));
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ScheduledJobDto | null>(null);
  const [history, setHistory] = useState<ScheduledJobDto | null>(null);

  const query = useQuery({
    queryKey: ['scheduled-jobs'] as const,
    queryFn: ({ signal }) => apiRequest<ScheduledJobsResponse>('/api/system/scheduled-jobs', { signal }),
    refetchInterval: 60_000,
  });

  const runNow = useMutation({
    mutationFn: ({ job, todayRun }: { job: ScheduledJobDto; todayRun?: TodayRun }) =>
      apiRequest<{ run: ScheduledJobRunDto; todayRunSkipped?: boolean }>(`/api/system/scheduled-jobs/${encodeURIComponent(job.key)}/run`, {
        method: 'POST',
        body: { confirm: true, ...(todayRun ? { todayRun } : {}) },
      }),
    onSuccess: ({ run, todayRunSkipped }, { job, todayRun }) => {
      const ok = run.status === 'ok' || run.status === 'skipped' || run.status === 'partial';
      // Asked to skip today's run, but this one didn't finish: the usual run picks up the rest.
      const kept = todayRun === 'skip' && !todayRunSkipped ? ' It didn’t finish, so today’s usual run will still happen.' : '';
      toast(`${job.label}: ${run.summary ?? run.error ?? RUN_LABEL[run.status].label}${kept}`, { variant: ok ? 'success' : 'error' });
      void queryClient.invalidateQueries({ queryKey: ['scheduled-jobs'] });
    },
    onError: (err) => toast(err instanceof ApiError ? err.message : 'Could not run the job.', { variant: 'error' }),
  });

  const onRunNow = async (job: ScheduledJobDto) => {
    const q = runNowQuestion(job);
    if (q.kind === 'choice') {
      // A daily job that sends messages, before its time: ask about today's usual run.
      const choice: { current: TodayRun } = { current: 'keep' };
      const ok = await confirm({
        title: `Run “${job.label}” now?`,
        description: `Today’s usual run is at ${q.time}.`,
        body: <TodayRunChoice time={q.time} onChange={(v) => (choice.current = v)} />,
        confirmLabel: 'Run now',
      });
      if (ok) runNow.mutate({ job, todayRun: choice.current });
      return;
    }
    const ok = await confirm({ title: `Run “${job.label}” now?`, description: q.description, confirmLabel: 'Run now' });
    if (ok) runNow.mutate({ job });
  };

  const skip = useMutation({
    mutationFn: ({ job, reason }: { job: ScheduledJobDto; reason: string }) =>
      apiRequest(`/api/system/scheduled-jobs/${encodeURIComponent(job.key)}/skip-next`, {
        method: 'POST',
        body: reason ? { reason } : {},
      }),
    onSuccess: (_r, { job }) => {
      toast(`“${job.label}”: the next run will be skipped.`, { variant: 'success' });
      void queryClient.invalidateQueries({ queryKey: ['scheduled-jobs'] });
    },
    onError: (err) => toast(err instanceof ApiError ? err.message : 'Could not skip the run.', { variant: 'error' }),
  });
  const undoSkip = useMutation({
    mutationFn: (job: ScheduledJobDto) => apiRequest(`/api/system/scheduled-jobs/${encodeURIComponent(job.key)}/skip-next`, { method: 'DELETE' }),
    onSuccess: (_r, job) => {
      toast(`“${job.label}” will run as usual.`, { variant: 'success' });
      void queryClient.invalidateQueries({ queryKey: ['scheduled-jobs'] });
    },
    onError: (err) => {
      toast(err instanceof ApiError ? err.message : 'Could not undo.', { variant: 'error' });
      void queryClient.invalidateQueries({ queryKey: ['scheduled-jobs'] });
    },
  });

  const onSkip = async (job: ScheduledJobDto) => {
    if (!job.skipTarget) return;
    const reason = { current: '' };
    const ok = await confirm({
      title: `Skip the next run of “${job.label}”?`,
      description: `The run ${runWhen(job.skipTarget.runAt)} won’t happen. After that, it runs as usual. You can undo this until then.`,
      body: <SkipReason warning={job.skipTarget.warning} onChange={(v) => (reason.current = v)} />,
      confirmLabel: 'Skip this run',
    });
    if (ok) skip.mutate({ job, reason: reason.current.trim() });
  };

  const groups = useMemo(() => {
    const map = new Map<string, ScheduledJobDto[]>();
    for (const j of query.data?.jobs ?? []) map.set(j.group, [...(map.get(j.group) ?? []), j]);
    return [...map.entries()];
  }, [query.data]);
  const problems = (query.data?.jobs ?? []).filter((j) => j.state === 'failed' || j.state === 'retrying');

  return (
    <ListShell
      title="Scheduled jobs"
      description="Things the portal does by itself at set times — creating each month’s fees, sending reminders and more. All times are India time."
    >
      <div className="space-y-4">
        <section className={clsx(card, 'border-gold-500/60 px-4 py-3')} aria-labelledby="free-plan-note">
          <h2 id="free-plan-note" className="flex items-center gap-2 text-sm font-semibold">
            <Info size={16} aria-hidden="true" /> About scheduled jobs on the free Cloudflare plan
          </h2>
          <div className="mt-1 space-y-1.5 text-sm text-[color:var(--color-text-muted)]">
            <p>
              Cloudflare’s free plan allows <b className="text-[color:var(--color-text)]">5 scheduled triggers for the whole account</b>, shared by
              every app on it. Babul Ilm uses <b className="text-[color:var(--color-text)]">1</b>: the server checks once an hour and runs
              whatever is due. Changing times on this page doesn’t use any more triggers.
            </p>
            <p>
              Don’t add triggers in Cloudflare without checking the account total. If the account goes over 5, Cloudflare rejects the change.
            </p>
            <p>
              Each hourly check can only do a limited amount of work on the free plan (about 50 steps, counting database work as well as
              emails and notifications). Jobs that send a lot work in batches and finish over the next few hours, and anything that doesn’t
              fit in one check runs at the next.
            </p>
          </div>
        </section>

        <section className={clsx(card, 'px-4 py-3')} aria-labelledby="if-fails">
          <h2 id="if-fails" className="text-sm font-semibold">
            When a job fails
          </h2>
          <p className="mt-1 text-sm text-[color:var(--color-text-muted)]">
            It tries again at the next hourly check, up to 3 times a day. If it still can’t, super admins get a portal notification (and an email
            when automatic emails are on), and the job shows red here. Jobs that send messages never send the same message twice.
          </p>
        </section>

        {query.isLoading ? (
          <div className="grid gap-3">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : query.isError || !query.data ? (
          <ErrorState
            title="Could not load scheduled jobs"
            description={query.error instanceof ApiError ? query.error.message : undefined}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <>
            <Heartbeat heartbeat={query.data.heartbeat} />
            <EmailBanner status={query.data.email} />
            {problems.length > 0 ? (
              <p role="status" className="flex items-start gap-2 rounded-lg bg-[color:var(--color-danger)]/10 px-4 py-3 text-sm text-[color:var(--color-danger)]">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
                <span>
                  {problems.length === 1 ? `“${problems[0]!.label}” is having trouble.` : `${problems.length} jobs are having trouble.`} Open
                  History to see what went wrong.
                </span>
              </p>
            ) : null}
            {groups.map(([group, jobs]) => (
              <section key={group} className={card} aria-label={group}>
                <h2 className="border-b border-[color:var(--color-border)] px-4 py-2.5 text-sm font-semibold">{group}</h2>
                <ul className="divide-y divide-dashed divide-[color:var(--color-border)]">
                  {jobs.map((job) => (
                    <JobRow
                      key={job.key}
                      job={job}
                      canEdit={canEdit}
                      running={runNow.isPending && runNow.variables?.job.key === job.key}
                      onEdit={() => setEditing(job)}
                      onHistory={() => setHistory(job)}
                      onRun={() => void onRunNow(job)}
                      onSkip={() => void onSkip(job)}
                      onUndoSkip={() => undoSkip.mutate(job)}
                      skipBusy={(skip.isPending && skip.variables?.job.key === job.key) || (undoSkip.isPending && undoSkip.variables?.key === job.key)}
                    />
                  ))}
                </ul>
              </section>
            ))}
          </>
        )}
      </div>

      <EditJobDrawer job={editing} onClose={() => setEditing(null)} />
      <HistoryDrawer job={history} onClose={() => setHistory(null)} />
    </ListShell>
  );
}

function Heartbeat({ heartbeat }: { heartbeat: ScheduledJobsResponse['heartbeat'] }) {
  if (!heartbeat.lastTickAt) {
    return (
      <p className="flex items-center gap-2 text-sm text-[color:var(--color-text-muted)]">
        <Clock size={14} aria-hidden="true" /> Waiting for the first hourly check.
      </p>
    );
  }
  if (!heartbeat.healthy) {
    return (
      <p role="alert" className="flex items-start gap-2 rounded-lg bg-[color:var(--color-danger)]/10 px-4 py-3 text-sm text-[color:var(--color-danger)]">
        <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
        <span>
          <b>Scheduled jobs aren’t running.</b> The last hourly check was {formatWhen(heartbeat.lastTickAt)}. Please tell whoever looks after the portal’s Cloudflare account.
        </span>
      </p>
    );
  }
  return (
    <p className="flex items-center gap-2 text-sm text-[color:var(--color-text-muted)]">
      <CheckCircle2 size={14} className="text-[color:var(--color-success)]" aria-hidden="true" /> Last hourly check: {formatWhen(heartbeat.lastTickAt)}.
    </p>
  );
}

function JobRow({
  job,
  canEdit,
  running,
  onEdit,
  onHistory,
  onRun,
  onSkip,
  onUndoSkip,
  skipBusy,
}: {
  job: ScheduledJobDto;
  canEdit: boolean;
  running: boolean;
  onEdit: () => void;
  onHistory: () => void;
  onRun: () => void;
  onSkip: () => void;
  onUndoSkip: () => void;
  skipBusy: boolean;
}) {
  const state = STATE[job.state];
  return (
    <li className="flex flex-col gap-2 px-4 py-3 md:flex-row md:items-start md:justify-between">
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2 font-semibold">
          {job.label}
          <span className={clsx(pill, state.tone)}>{state.label}</span>
        </p>
        <p className="text-sm text-[color:var(--color-text-muted)]">{job.description}</p>
        <p className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-[color:var(--color-text-muted)]">
          <span className="font-medium text-[color:var(--color-text)]">{scheduleWords(job.schedule)}</span>
          {job.dueNow && job.nextRunAt ? (
            <span>Due now — runs at the next hourly check ({formatWhen(job.nextRunAt)})</span>
          ) : job.nextRunAt ? (
            <span>Next: {formatWhen(job.nextRunAt)}</span>
          ) : null}
          {job.lastRun ? (
            <span>
              Last: {formatWhen(job.lastRun.finishedAt ?? job.lastRun.startedAt)}
              {job.lastRun.summary ? ` — ${job.lastRun.summary}` : ''}
            </span>
          ) : null}
        </p>
        {job.nextSkip ? (
          <p className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-[color:var(--color-warning)]">
            <SkipForward size={12} aria-hidden="true" />
            <span>
              The run {runWhen(job.nextSkip.runAt)} will be skipped
              {job.nextSkip.byName ? ` (by ${job.nextSkip.byName})` : ''}
              {job.nextSkip.reason ? ` — ${job.nextSkip.reason}` : ''}
            </span>
            {canEdit ? (
              <button
                type="button"
                onClick={onUndoSkip}
                disabled={skipBusy}
                className="inline-flex items-center gap-1 font-semibold underline disabled:opacity-60"
              >
                <Undo2 size={12} aria-hidden="true" /> Undo
              </button>
            ) : null}
          </p>
        ) : null}
        {job.needs && !job.needs.on ? (
          <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">Runs once {job.needs.label} is switched on.</p>
        ) : null}
        {job.state === 'failed' || job.state === 'retrying' ? (
          <p className="mt-1 text-xs text-[color:var(--color-danger)]">{job.lastRun?.error ?? 'It didn’t run in time.'}</p>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-wrap gap-2">
        <Button size="sm" variant="ghost" icon={<History size={14} aria-hidden="true" />} onClick={onHistory}>
          History
        </Button>
        {canEdit ? (
          <>
            <Button size="sm" variant="secondary" icon={<Settings2 size={14} aria-hidden="true" />} onClick={onEdit}>
              Change
            </Button>
            <Button size="sm" variant="secondary" icon={<Play size={14} aria-hidden="true" />} loading={running} onClick={onRun} disabled={job.needs?.on === false}>
              Run now
            </Button>
            {job.skipTarget && !job.nextSkip ? (
              <Button size="sm" variant="ghost" icon={<SkipForward size={14} aria-hidden="true" />} loading={skipBusy} onClick={onSkip}>
                Skip next run
              </Button>
            ) : null}
          </>
        ) : null}
      </div>
    </li>
  );
}

type TodayRun = 'keep' | 'skip';

/** The choice in the "Run now" box: still do today's usual run, or skip it. */
function TodayRunChoice({ time, onChange }: { time: string; onChange: (v: TodayRun) => void }) {
  const [value, setValue] = useState<TodayRun>('keep');
  const pick = (v: TodayRun) => {
    setValue(v);
    onChange(v);
  };
  const option = (v: TodayRun, label: string, hint?: string) => (
    <label className="flex cursor-pointer items-start gap-3 text-sm">
      <input
        type="radio"
        name="today-run"
        checked={value === v}
        onChange={() => pick(v)}
        className="mt-0.5 h-4 w-4 shrink-0 accent-gold-500"
      />
      <span>
        <span className="font-medium">{label}</span>
        {hint ? <span className="block text-xs text-[color:var(--color-text-muted)]">{hint}</span> : null}
      </span>
    </label>
  );
  return (
    <fieldset className="space-y-2">
      <legend className="sr-only">Today’s usual run</legend>
      {option('keep', `Still run at ${time}`, 'It only messages people who haven’t been messaged yet.')}
      {option('skip', `Skip today’s ${time} run`)}
    </fieldset>
  );
}

/** "Skip next run": an optional reason, and what skipping means for this job. */
function SkipReason({ warning, onChange }: { warning: string | null; onChange: (v: string) => void }) {
  return (
    <div className="space-y-3">
      {warning ? (
        <p className="flex items-start gap-2 rounded-md bg-[color:var(--color-warning)]/10 px-3 py-2 text-sm">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-[color:var(--color-warning)]" aria-hidden="true" />
          <span>{warning}</span>
        </p>
      ) : null}
      <label className="block text-sm">
        <span className="font-medium">Reason (optional)</span>
        <input
          type="text"
          maxLength={200}
          placeholder="For example: we told families on WhatsApp"
          onChange={(e) => onChange(e.target.value)}
          className={clsx(selectClass, 'mt-1')}
        />
      </label>
    </div>
  );
}

function EditJobDrawer({ job, onClose }: { job: ScheduledJobDto | null; onClose: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(true);
  const [schedule, setSchedule] = useState<JobSchedule | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  useEffect(() => {
    if (job) {
      setEnabled(job.enabled);
      setSchedule(job.schedule);
      setProblem(null);
    }
  }, [job]);

  const save = useMutation({
    mutationFn: () =>
      apiRequest(`/api/system/scheduled-jobs/${encodeURIComponent(job!.key)}`, {
        method: 'PATCH',
        body: {
          ...(job!.canDisable ? { enabled } : {}),
          frequency: schedule!.frequency,
          ...(schedule!.frequency !== 'hourly' && schedule!.hour !== null ? { hour: schedule!.hour } : {}),
          ...(schedule!.frequency === 'weekly' ? { weekday: schedule!.weekday ?? 0 } : {}),
          ...(schedule!.frequency === 'monthly' ? { dayOfMonth: schedule!.dayOfMonth ?? 1 } : {}),
        },
      }),
    onSuccess: () => {
      toast('Saved. The next hourly check uses the new time.', { variant: 'success' });
      void queryClient.invalidateQueries({ queryKey: ['scheduled-jobs'] });
      onClose();
    },
    onError: (err) => setProblem(err instanceof ApiError ? err.message : 'Could not save.'),
  });

  if (!job || !schedule) return null;
  const hours = Array.from({ length: job.allowedHours.to - job.allowedHours.from + 1 }, (_, i) => job.allowedHours.from + i);
  const set = (patch: Partial<JobSchedule>) => {
    setSchedule((s) => (s ? { ...s, ...patch } : s));
    setProblem(null);
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={job.label}
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-[color:var(--color-text-muted)]">{job.description}</p>

        {job.canDisable ? (
          <label className="flex items-center justify-between gap-3 rounded-md border border-[color:var(--color-border)] px-3 py-2">
            <span className="text-sm font-medium">Switched on</span>
            <input type="checkbox" className="h-5 w-5 accent-gold-700" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          </label>
        ) : (
          <p className="text-xs text-[color:var(--color-text-muted)]">This job can’t be switched off — the portal relies on it.</p>
        )}

        {job.allowedFrequencies.length > 1 ? (
          <label className="block text-sm font-medium">
            How often
            <select className={clsx(selectClass, 'mt-1')} value={schedule.frequency} onChange={(e) => set({ frequency: e.target.value as JobFrequency })}>
              {job.allowedFrequencies.map((f) => (
                <option key={f} value={f}>
                  {FREQUENCY_LABEL[f]}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <p className="text-sm">
            <span className="font-medium">How often:</span> {FREQUENCY_LABEL[schedule.frequency]}
          </p>
        )}

        {schedule.frequency === 'weekly' ? (
          <label className="block text-sm font-medium">
            Day of the week
            <select className={clsx(selectClass, 'mt-1')} value={schedule.weekday ?? 0} onChange={(e) => set({ weekday: Number(e.target.value) })}>
              {WEEKDAYS.map((d, i) => (
                <option key={d} value={i}>
                  {d}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {schedule.frequency === 'monthly' ? (
          <label className="block text-sm font-medium">
            Day of the month
            <select className={clsx(selectClass, 'mt-1')} value={schedule.dayOfMonth ?? 1} onChange={(e) => set({ dayOfMonth: Number(e.target.value) })}>
              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d}>
                  {ordinal(d)}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {schedule.frequency !== 'hourly' ? (
          <label className="block text-sm font-medium">
            Time (India time)
            <select className={clsx(selectClass, 'mt-1')} value={schedule.hour ?? hours[0]} onChange={(e) => set({ hour: Number(e.target.value) })}>
              {hours.map((h) => (
                <option key={h} value={h}>
                  {hourWords(h)}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {job.scheduleNote ? <p className="text-xs text-[color:var(--color-text-muted)]">{job.scheduleNote}</p> : null}
        <p className="rounded-md bg-[color:var(--color-surface-strong)] px-3 py-2 text-xs text-[color:var(--color-text-muted)]">
          <b>If it fails:</b> {job.ifItFails}
        </p>
        {problem ? (
          <p role="alert" className="text-sm text-[color:var(--color-danger)]">
            {problem}
          </p>
        ) : null}
      </div>
    </Drawer>
  );
}

function HistoryDrawer({ job, onClose }: { job: ScheduledJobDto | null; onClose: () => void }) {
  const query = useQuery({
    queryKey: ['scheduled-jobs', 'runs', job?.key] as const,
    enabled: !!job,
    queryFn: ({ signal }) => apiRequest<{ items: ScheduledJobRunDto[] }>(`/api/system/scheduled-jobs/${encodeURIComponent(job!.key)}/runs`, { signal }),
  });
  return (
    <Drawer open={!!job} onClose={onClose} title={job ? `${job.label} — history` : 'History'} size="md">
      {query.isLoading ? (
        <SkeletonCard />
      ) : query.isError ? (
        <ErrorState
          title="Could not load the history"
          description={query.error instanceof ApiError ? query.error.message : undefined}
          onRetry={() => void query.refetch()}
        />
      ) : (query.data?.items.length ?? 0) === 0 ? (
        <p className="text-sm text-[color:var(--color-text-muted)]">It hasn’t run yet.</p>
      ) : (
        <ul className="divide-y divide-dashed divide-[color:var(--color-border)]">
          {query.data!.items.map((r) => {
            const s = RUN_LABEL[r.status];
            return (
              <li key={r.id} className="py-2">
                <p className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium">{formatWhen(r.startedAt)}</span>
                  <span className={clsx(pill, s.tone)}>{s.label}</span>
                  {r.trigger === 'manual' ? (
                    <span className="text-xs text-[color:var(--color-text-muted)]">Run now{r.startedByName ? ` by ${r.startedByName}` : ''}</span>
                  ) : r.trigger === 'retry' ? (
                    <span className="text-xs text-[color:var(--color-text-muted)]">Try {r.attempt}</span>
                  ) : null}
                </p>
                {r.summary ? <p className="text-sm text-[color:var(--color-text-muted)]">{r.summary}</p> : null}
                {r.error ? <p className="text-sm text-[color:var(--color-danger)]">{r.error}</p> : null}
              </li>
            );
          })}
        </ul>
      )}
    </Drawer>
  );
}
